import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';

import { DialogOutletService } from '@gomaterials/ui-components/api';
import { GoInputEmailModule } from '@gomaterials/ui-components/inputemail';
import { GoPasswordModule } from '@gomaterials/ui-components/password';
import { GoButtonModule } from '@gomaterials/ui-components/button';
import { GoImageModule } from '@gomaterials/ui-components/image';

import { AuthPageLayoutComponent } from '@layout/auth-page-layout/auth-page-layout.component';
import { APP_DIALOG_KEY, AppPaths } from '@core/constants';

import { AuthService } from '../../services';
import { AuthRequest, AuthUser } from '../../models';

@Component({
    selector: 'app-sign-in',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FaIconComponent,
        AuthPageLayoutComponent,
        GoInputEmailModule,
        GoPasswordModule,
        GoButtonModule,
        GoImageModule,
    ],
    templateUrl: './sign-in.component.html',
})
export class SignInComponent {
    private fb = inject(FormBuilder);
    private authService = inject(AuthService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private dialogOutletService = inject(DialogOutletService);

    loading = signal(false);

    faEnvelope = faEnvelope;
    formSubmitted = false;

    form = this.fb.group({
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(6)]],
    });

    onSubmit(): void {
        this.formSubmitted = true;
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.loading.set(true);

        this.authService
            .login(this.form.getRawValue() as AuthRequest)
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (authUser: AuthUser) => {
                    if (!authUser.token) {
                        if (authUser.remainingAttempts)
                            this.showLoginFailedDialog(this.getLoginErrorMessage(authUser, null));
                        return;
                    }
                    const returnUrl = this.activatedRoute.snapshot.queryParamMap.get('returnUrl');
                    this.router.navigateByUrl(returnUrl ?? '/' + AppPaths.searchPlants);
                },
                error: (err: Error) => {
                    this.showLoginFailedDialog(this.getLoginErrorMessage(null, err));
                },
            });
    }

    showLoginFailedDialog(message?: string): void {
        this.dialogOutletService.open({
            key: APP_DIALOG_KEY,
            severity: 'error',
            contentHeader: 'Login Failed',
            message: message,
            rejectVisible: false,
            acceptLabel: 'OK',
            size: 'sm',
        });
    }

    isInvalid(controlName: string) {
        const control = this.form.get(controlName);
        return !!control?.invalid && (control.touched || this.formSubmitted);
    }

    getLoginErrorMessage(authUser: AuthUser | null, error: Error | null) {
        return error
            ? error.message
            : `Authentication failed, invalid email or password. You have ${authUser?.remainingAttempts} login attempts remaining before you need to reset your password.`;
    }
}
